// ═══════════════════════════════════════════════════════════════════
//  menu.js — definición única del menú y sus permisos
//
//  Antes los permisos estaban duplicados en tres sitios (el array NAV de
//  AppLayout, los roles={[...]} de App.jsx y los requireRol del backend).
//  Ahora la parte de cliente sale de aquí: quien añada un módulo nuevo
//  toca este fichero y el catálogo `permisos` de la BD, y nada más.
//
//  permiso        → código del catálogo de permisos
//  permisoAlguno  → lista; basta con tener uno (entradas que agrupan varias
//                   pantallas, como la agenda completa)
// ═══════════════════════════════════════════════════════════════════

export const MENU = [
  {
    grupo: 'Principal',
    items: [
      { to: '/dashboard',     label: 'Dashboard',     permiso: 'dashboard',     icono: 'home' },
      { to: '/mis-servicios', label: 'Mis servicios', permiso: 'mis_servicios', icono: 'bus'  },
      // Las hojas de ruta que se hace el conductor de un servicio que no está
      // dado de alta. Permiso propio: no todo el que ve sus servicios tiene
      // por qué poder expedir documentos.
      { to: '/hojas-ruta',    label: 'Hojas de ruta', permiso: 'hojas_ruta',    icono: 'doc'  },
      // Las autorizaciones de las rutas recurrentes (escolar/obrera) en PDF,
      // para que el conductor las enseñe si le paran (entrega 291).
      { to: '/autorizaciones-ruta', label: 'Autorizaciones de ruta', permiso: 'autorizaciones_ruta', icono: 'doc' },
      // Su DNI, carnet y CAP, que se los sube él (entrega 304).
      { to: '/mi-documentacion', label: 'Mi documentación', permiso: 'mi_documentacion', icono: 'user' },
    ],
  },
  {
    grupo: 'Operativa',
    items: [
      { to: '/presupuestos', label: 'Presupuestos', permiso: 'presupuestos', icono: 'doc' },
      // La entrada "Agenda" a secas se ha quitado: las dos variantes ya cubren
      // todo y tenerla arriba solo repetía lo mismo sin filtrar. La RUTA sigue
      // existiendo (/agenda sin ?tipo=), porque el Dashboard entra por ahí.
      { to: '/agenda?tipo=recurrente',   label: 'Agenda recurrente',   permiso: 'agenda_recurrente',   icono: 'cal' },
      { to: '/agenda?tipo=discrecional', label: 'Agenda discrecional', permiso: 'agenda_discrecional', icono: 'cal' },
      { to: '/trafico',      label: 'Tráfico',               permiso: 'trafico',      icono: 'grid' },
      { to: '/calendarios',  label: 'Calendarios',           permiso: 'calendarios',  icono: 'cal'  },
      { to: '/recurrentes',  label: 'Servicios recurrentes', permiso: 'recurrentes',  icono: 'grid' },
    ],
  },
  {
    grupo: 'Datos maestros',
    items: [
      { to: '/entidades',        label: 'Entidades',        permiso: 'entidades',        icono: 'building' },
      { to: '/clientes',         label: 'Clientes',         permiso: 'clientes',         icono: 'users'    },
      { to: '/vehiculos',        label: 'Vehículos',        permiso: 'vehiculos',        icono: 'truck'    },
      { to: '/trabajadores',     label: 'Trabajadores',     permiso: 'trabajadores',     icono: 'hardhat'  },
      { to: '/documentacion-personal', label: 'Documentación del personal', permiso: 'documentacion_personal', icono: 'doc' },
      { to: '/ausencias',        label: 'Ausencias',        permiso: 'ausencias',        icono: 'cal'      },
      { to: '/otros-trabajos',   label: 'Otros trabajos',   permiso: 'otros_trabajos',   icono: 'grid'     },
      { to: '/resumen-personal', label: 'Resumen personal', permiso: 'resumen_personal', icono: 'cal'      },
      // Los colores con que se pintan los estados en todas las pantallas
      // (entrega 278). Permiso propio: no los cambia cualquiera.
      { to: '/colores-estado',   label: 'Colores de estados', permiso: 'estados_color',  icono: 'palette'  },
    ],
  },
  {
    grupo: 'Histórico',
    items: [
      { to: '/historico', label: 'Servicios realizados', icono: 'list',
        permisoAlguno: ['historico_recurrente', 'historico_discrecional'] },
      { to: '/historico?tipo=recurrente',   label: 'Realizados recurrentes',    permiso: 'historico_recurrente',   icono: 'list' },
      { to: '/historico?tipo=discrecional', label: 'Realizados discrecionales', permiso: 'historico_discrecional', icono: 'list' },
      { to: '/extras', label: 'Extras trabajadores', permiso: 'extras', icono: 'plus' },
    ],
  },
  {
    grupo: 'Administración',
    items: [
      // Vale con CUALQUIERA de los cuatro. Pedía solo el paraguas 'avisos',
      // así que a un conductor con 'avisos_trabajador' —el que le deja ver y
      // renovar SUS papeles— no le salía la entrada, y si escribía la
      // dirección a mano la aplicación lo echaba a otra pantalla. El
      // servidor sí le dejaba entrar: era la pantalla la que no.
      { to: '/avisos',      label: 'Avisos caducidad', icono: 'bell',
        permisoAlguno: ['avisos','avisos_trabajador','avisos_vehiculo','avisos_empresa','avisos_autorizaciones'] },
      { to: '/usuarios',    label: 'Usuarios',         permiso: 'usuarios',    icono: 'user'     },
      { to: '/roles',       label: 'Roles y permisos', permiso: 'roles',       icono: 'shield'   },
      { to: '/tipos-aviso', label: 'Tipos de aviso',   permiso: 'tipos_aviso', icono: 'settings' },
    ],
  },
];

// ─── ¿Puede ver esta entrada? ─────────────────────────────────────
export function entradaVisible(item, puede) {
  if (item.permisoAlguno) return item.permisoAlguno.some(c => puede(c, 'ver'));
  return puede(item.permiso, 'ver');
}

// ─── Primera ruta accesible ───────────────────────────────────────
// Se usa al entrar y como destino cuando alguien intenta abrir una
// pantalla que no puede ver. Así un conductor cae en "Mis servicios"
// y no en un dashboard vacío al que no tiene acceso.
export function rutaInicial(puede) {
  // Primera pasada: entradas "limpias", sin ?tipo=
  for (const grupo of MENU) {
    for (const item of grupo.items) {
      if (item.to.includes('?')) continue;
      if (entradaVisible(item, puede)) return item.to;
    }
  }
  // Segunda pasada: las variantes con ?tipo=. Hace falta desde que se quitó la
  // entrada "Agenda" a secas: quien solo tenga permiso de agenda no tenía
  // ninguna entrada sin ?tipo= y se quedaba sin pantalla de inicio.
  for (const grupo of MENU) {
    for (const item of grupo.items) {
      if (entradaVisible(item, puede)) return item.to;
    }
  }
  return '/perfil';   // sin permisos de nada: solo su propio perfil
}
